import React, { useEffect, useImperativeHandle, useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from '@roger/ui-components';
import { Stethoscope } from 'lucide-react';
import type { UserType } from '../../services/api/models/UserType';
import { USER_TYPE_OPTIONS } from '../../constants/userTypes';
import { updateUserType } from '../../services/api';
import { useStore } from '../../store';
import { useShallow } from 'zustand/react/shallow';
import { RoleAssignmentSaveError } from '../rbac/role-assignment-dialog/useRoleAssignmentDialog';

const NONE_VALUE = '__none__';

export type UserTypeSectionHandle = {
  saveIfDirty: () => Promise<void>;
};

type UserTypeSectionProps = {
  userId: string;
  userType: UserType | null | undefined;
  /** When false, omit the leading separator (e.g. first body section). */
  showTopSeparator?: boolean;
};

export const UserTypeSection = React.memo(
  React.forwardRef<UserTypeSectionHandle, UserTypeSectionProps>(
    function UserTypeSection(
      { userId, userType, showTopSeparator = true },
      ref
    ) {
      const { showError } = useStore(
        useShallow(state => ({ showError: state.showError }))
      );
      const [value, setValue] = useState<UserType | null>(userType ?? null);

      useEffect(() => {
        setValue(userType ?? null);
      }, [userType]);

      const isDirty = (value ?? null) !== (userType ?? null);

      useImperativeHandle(
        ref,
        () => ({
          saveIfDirty: async () => {
            if (!isDirty) return;
            try {
              await updateUserType({
                path: { user_id: userId },
                body: { user_type: value },
                throwOnError: true,
              });
            } catch (error) {
              console.error('Failed to update user type:', error);
              showError(
                'Save Failed',
                'Unable to update user type. Please try again.'
              );
              throw new RoleAssignmentSaveError('user type save failed');
            }
          },
        }),
        [isDirty, showError, userId, value]
      );

      return (
        <div className='shrink-0'>
          {showTopSeparator && <Separator className='my-4' />}
          <div className='space-y-3'>
            <div className='flex items-center gap-2'>
              <Stethoscope className='h-4 w-4 text-muted-foreground' />
              <h3 className='text-sm font-semibold'>Clinical Title</h3>
            </div>
            <Select
              value={value ?? NONE_VALUE}
              onValueChange={next => {
                setValue(next === NONE_VALUE ? null : (next as UserType));
              }}
            >
              <SelectTrigger
                id='user-type-select'
                aria-label='Clinical title'
                selectSize='sm'
                className='w-full md:w-72'
              >
                <SelectValue placeholder='Not set' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE_VALUE}>Not set</SelectItem>
                {USER_TYPE_OPTIONS.map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Separator className='my-4' />
        </div>
      );
    }
  )
);
